import { ErrorMessage } from '@components/Form'
import {
  ACCEPTED_FILE_EXTENSIONS_FOR_INPUT,
  displayName,
  type GeospatialFile,
  groupPickedFiles
} from '@lib/GeospatialFile'
import { PlanFileReadError } from 'api-validations/crusher'
import { getPlanningCffsApi, PlanAsset } from 'api-validations/locutus'
import { MeasureSystem } from 'api-validations/shared'
import { type ControllableApplication } from 'api-validations/stitch'
import { type ChangeEvent, type FC, useReducer, useState } from 'react'
import { useOrgCodeOrThrow } from 'shared-ui/active-operation'
import { usePanoramaQuery } from 'shared-ui/api-provider'
import {
  ButtonKind,
  ButtonSize,
  CircleSpinner,
  Icon,
  PanoramaButton,
  sectionCardClasses
} from 'shared-ui/components'
import { SpriteMap } from 'shared-ui/global'
import { createVariants } from 'shared-ui/lib'
import { useLocalization } from 'shared-ui/localization'
import { isNonNil } from 'utils'

import { useCreatePlanAssets } from '../../api/useCreatePlanAssets'
import { useReadPlanFile } from '../../api/useReadPlanFile'
import { toInspectRequest } from '../../lib/planUpload/inspectRequest'
import {
  DEFAULT_APPLICATION_TYPE,
  type PlanFileReading,
  type PlanningField,
  type UploadAssetType
} from '../../lib/planUpload/planEntry'
import {
  EMPTY_PLAN_UPLOAD,
  initialRead,
  shownEntries,
  ReadActivity,
  readActivity,
  PlanUploadActionType,
  planUploadReducer,
  type PlanReadProblem,
  type PlanUploadSource,
  ReadStatus,
  CreateStatus,
  DestinationKind,
  type PlanUploadRow,
  rereadReplacements,
  SubmitStatus,
  toCreatePlan,
  toImportSummary,
  toRows
} from '../../lib/planUpload/planUploadReducer'
import { ImportBand } from './ImportBand'
import { PlanEntryRow, PlanEntryRowHeader, type RowPlacement } from './PlanEntryRow'

const FILE_INPUT_ID = 'plan-asset-upload'

const containerClasses = createVariants({
  base: 'flex flex-col',
  variants: {
    destination: {
      [DestinationKind.Operation]: sectionCardClasses({ className: 'p-0' }),
      [DestinationKind.Field]: 'w-full gap-y-3'
    }
  }
})

/**
 * Past this many rows, we offer a band at the bottom of the table as well.
 */
const ROWS_WORTH_REPEATING = 12

/**
 * Stable dummy used while the field list loads.
 */
const NO_FIELDS: readonly PlanningField[] = []

/**
 * A source still missing shapefile parts. We request what we need.
 */
const MissingParts: FC<{
  source: PlanUploadSource
  onSupply: (source: PlanUploadSource, file: GeospatialFile) => void
}> = ({ source, onSupply }) => {
  const { t, tByPath } = useLocalization()
  const request = toInspectRequest(source)
  if (request.readable) return null

  return (
    <div className="mx-5 mb-4 rounded-lg border border-red-200 bg-red-50 p-4">
      <div className="pan-title5 mb-2 flex items-center gap-2">
        <Icon icon={SpriteMap.AlertIcon} className="size-5 text-red-600" />
        {displayName(source.file)}
      </div>
      <div className="pan-body flex flex-col gap-y-2">
        {request.reasons.map(({ message, extension }) => (
          <div key={`${message.path}#${message.filename}`} className="flex items-center gap-x-4">
            <p>{tByPath(message.path, message.defaultValue, { filename: message.filename })}</p>
            {isNonNil(extension) && (
              <>
                <PanoramaButton
                  as="label"
                  htmlFor={message.filename}
                  kind={ButtonKind.Tertiary}
                  size={ButtonSize.Small}
                  className="cursor-pointer"
                  icon={SpriteMap.UploadIcon}
                >
                  {t(($) => $.buttons.import)}
                </PanoramaButton>
                <input
                  type="file"
                  id={message.filename}
                  accept={`.${extension}`}
                  hidden
                  onChange={(event) => {
                    const file = event.target.files?.[0]
                    if (isNonNil(file)) onSupply(source, source.file.appendFile(file))
                  }}
                />
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * Entries of one source to read again under one reading.
 */
type Reinterpretation = { entryIds: readonly string[]; reading: PlanFileReading }

export type ImportedPrescription = { id: string; name: string; type: ControllableApplication }

type Props = { disabled: boolean } & (
  | {
      destination: DestinationKind.Operation
      /**
       * Default shapefile asset type. Inferred from Boundaries or Prescriptions page.
       */
      defaultAssetType: UploadAssetType
    }
  | {
      destination: DestinationKind.Field
      fieldId: string
      onImported: (result: { created: readonly ImportedPrescription[]; failed: number }) => void
    }
)

/**
 * Imports boundaries and prescriptions from picked files.
 */
export const PlanAssetUpload: FC<Props> = (props) => {
  const { disabled } = props
  const defaultAssetType =
    props.destination === DestinationKind.Operation
      ? props.defaultAssetType
      : PlanAsset.Prescription
  const { t, measureSystem } = useLocalization()
  const orgCode = useOrgCodeOrThrow()
  const [state, dispatch] = useReducer(planUploadReducer, EMPTY_PLAN_UPLOAD)
  // The units of all shapefile rate columns in this import.
  const [importMeasureSystem, setImportMeasureSystem] = useState(measureSystem)
  const readPlanFile = useReadPlanFile()
  const createPlanAssets = useCreatePlanAssets()

  const planningFields = usePanoramaQuery(getPlanningCffsApi, { orgCode })
  const fields = planningFields.data?.items ?? NO_FIELDS

  const rows = toRows({
    state,
    destination:
      props.destination === DestinationKind.Field
        ? { kind: DestinationKind.Field, field: fields.find(({ id }) => id === props.fieldId) }
        : { kind: DestinationKind.Operation, fields }
  })
  const createPlan = toCreatePlan({ state, rows })
  const locked = state.submission.status !== SubmitStatus.Idle
  const included = rows.filter(({ excluded }) => !excluded)
  // Shapefile prescription rates must be declared.
  const hasDeclarableRates = rows.some(
    ({ entry, assetType }) => !entry.assetTypeIsSettled && assetType === PlanAsset.Prescription
  )
  const defaultReading: PlanFileReading =
    defaultAssetType === PlanAsset.Boundary
      ? { assetType: PlanAsset.Boundary }
      : {
          assetType: PlanAsset.Prescription,
          applicationType: DEFAULT_APPLICATION_TYPE,
          measureSystem: importMeasureSystem
        }

  const readSource = async (source: PlanUploadSource) => {
    const request = toInspectRequest(source)
    if (!request.readable) return

    await readPlanFile({ source, request }).then(
      ({ entries, unreadable }) =>
        dispatch({
          type: PlanUploadActionType.SourceRead,
          sourceId: source.id,
          entries,
          unreadable
        }),
      (problem: PlanReadProblem) =>
        dispatch({ type: PlanUploadActionType.SourceFailed, sourceId: source.id, problem })
    )
  }

  const pickFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const picked = groupPickedFiles(Array.from(event.target.files ?? []))
    event.target.value = ''
    if (picked.length === 0) return

    const sources = picked.map((file) => {
      const source = { id: crypto.randomUUID(), file, reading: defaultReading }
      return { ...source, read: initialRead(source) }
    })

    dispatch({ type: PlanUploadActionType.Picked, sources })
    sources.forEach(readSource)
  }

  const supplyPart = (source: PlanUploadSource, file: GeospatialFile) => {
    // Read with the import's current units, which may have changed since the source was picked.
    dispatch({
      type: PlanUploadActionType.PartSupplied,
      sourceId: source.id,
      file,
      reading: defaultReading
    })
    readSource({ ...source, file, reading: defaultReading })
  }

  /**
   * Re-reads some of a source's assets under a new reading, since crusher applies a shapefile's
   * declared asset type and units during conversion. A source's re-reads land together or not at all.
   */
  const readAs = async (
    source: PlanUploadSource,
    reinterpretations: readonly Reinterpretation[]
  ) => {
    const shown = shownEntries(source.read)
    if (shown === undefined) return

    const reads = reinterpretations.flatMap(({ entryIds, reading }) => {
      const reread = { ...source, reading }
      const request = toInspectRequest(reread)
      return request.readable ? [{ entryIds, reread, request }] : []
    })
    if (reads.length === 0) return

    dispatch({ type: PlanUploadActionType.Rereading, sourceId: source.id })

    await Promise.all(
      reads.map(async ({ entryIds, reread, request }) => {
        const { entries } = await readPlanFile({ source: reread, request })
        return rereadReplacements({ shown, reread: entries, entryIds })
      })
    ).then(
      (answers) => {
        const replacements = answers.flat()
        const everyRowFound = replacements.every(({ entries }) => entries.length > 0)

        dispatch(
          everyRowFound
            ? {
                type: PlanUploadActionType.EntriesReread,
                sourceId: source.id,
                replacements
              }
            : {
                type: PlanUploadActionType.RereadFailed,
                sourceId: source.id,
                problem: PlanFileReadError.Unknown
              }
        )
      },
      (problem: PlanReadProblem) =>
        dispatch({ type: PlanUploadActionType.RereadFailed, sourceId: source.id, problem })
    )
  }

  const changeMeasureSystem = (next: MeasureSystem) => {
    setImportMeasureSystem(next)

    // Re-read only declared shapefile prescriptions, one read per application type.
    for (const source of state.sources) {
      const byType = new Map<ControllableApplication, string[]>()

      for (const entry of shownEntries(source.read) ?? []) {
        if (entry.assetTypeIsSettled || entry.assetType !== PlanAsset.Prescription) continue
        byType.set(entry.applicationType, [...(byType.get(entry.applicationType) ?? []), entry.id])
      }

      readAs(
        source,
        [...byType].map(([applicationType, entryIds]) => ({
          entryIds,
          reading: { assetType: PlanAsset.Prescription, applicationType, measureSystem: next }
        }))
      )
    }
  }

  const importAssets = async () => {
    if (!createPlan.ready) return

    dispatch({
      type: PlanUploadActionType.CreateStarted,
      entryIds: createPlan.rows.map(({ entry }) => entry.id)
    })

    const created: ImportedPrescription[] = []
    let failedCount = 0

    try {
      await createPlanAssets({
        rows: createPlan.rows,
        newFields: createPlan.newFields,
        onSettled: (entryId, outcome) => {
          dispatch({ type: PlanUploadActionType.CreateProgressed, entryId, outcome })
          if (outcome.status === CreateStatus.Failed) failedCount += 1
          if (outcome.status !== CreateStatus.Created) return

          const row = createPlan.rows.find(({ entry }) => entry.id === entryId)
          if (row?.entry.assetType === PlanAsset.Prescription)
            created.push({
              id: outcome.createdId,
              name: row.name.trim(),
              type: row.entry.applicationType
            })
        }
      })
    } finally {
      dispatch({ type: PlanUploadActionType.CreateSettled })
    }

    if (props.destination === DestinationKind.Field)
      props.onImported({ created, failed: failedCount })
  }

  const placementFor = (row: PlanUploadRow): RowPlacement =>
    props.destination === DestinationKind.Field
      ? { kind: DestinationKind.Field }
      : {
          kind: DestinationKind.Operation,
          fields,
          onTarget: (target) =>
            dispatch({ type: PlanUploadActionType.Targeted, entryId: row.entry.id, target })
        }

  const unreadable = state.sources.filter(({ read }) => read.status === ReadStatus.Unreadable)
  const failed = state.sources.filter(({ read }) => read.status === ReadStatus.Failed)
  const empty = state.sources.filter(
    ({ read }) => read.status === ReadStatus.Ready && read.entries.length === 0
  )
  const rereadFailures = state.sources.flatMap((source) =>
    source.read.status === ReadStatus.Ready && source.read.rereadProblem !== undefined
      ? [{ source, problem: source.read.rereadProblem }]
      : []
  )
  const partial = state.sources.filter(
    ({ read }) => read.status === ReadStatus.Ready && read.entries.length > 0 && read.unreadable > 0
  )

  const bandProps = {
    summary: toImportSummary({ state, rows, createPlan }),
    createPlan,
    activity: readActivity(state),
    locked,
    rates: hasDeclarableRates
      ? { measureSystem: importMeasureSystem, onChange: changeMeasureSystem }
      : undefined,
    onClear: () => dispatch({ type: PlanUploadActionType.Cleared }),
    onImport: () => importAssets()
  }

  const picker = (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <PanoramaButton
          as="label"
          htmlFor={FILE_INPUT_ID}
          kind={ButtonKind.Secondary}
          icon={SpriteMap.UploadIcon}
          className={disabled || locked ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
        >
          {t(($) => $.fileUpload.uploadFiles)}
        </PanoramaButton>
        {readActivity(state) === ReadActivity.Reading && (
          <span className="pan-body flex items-center gap-2 text-gray-600">
            <CircleSpinner size="size-4" />
            {t(($) => $.planning.upload.reading)}
          </span>
        )}
      </div>
      <input
        type="file"
        id={FILE_INPUT_ID}
        accept={ACCEPTED_FILE_EXTENSIONS_FOR_INPUT}
        multiple
        hidden
        disabled={disabled || locked}
        onChange={pickFiles}
      />
    </>
  )

  return (
    <div className={containerClasses({ destination: props.destination })}>
      {props.destination === DestinationKind.Operation ? (
        <div className="flex flex-col gap-3 p-5">
          <h2 className="pan-title2 flex items-center gap-x-3 text-gray-900">
            <Icon icon={SpriteMap.CloudArrowIcon} className="text-green-500" />
            {t(($) => $.planning.upload.title)}
          </h2>
          <p className="text-gray-600">{t(($) => $.planning.upload.description)}</p>
          {picker}
        </div>
      ) : (
        <div className="flex flex-col items-center">{picker}</div>
      )}

      {unreadable.map((source) => (
        <MissingParts key={source.id} source={source} onSupply={supplyPart} />
      ))}

      {failed.map((source) => (
        <ErrorMessage key={source.id} className="mx-5">
          {displayName(source.file)}:{' '}
          {t(
            ($) =>
              $.planning.upload.unreadable[
                source.read.status === ReadStatus.Failed
                  ? source.read.problem
                  : PlanFileReadError.Unknown
              ]
          )}
        </ErrorMessage>
      ))}

      {empty.map((source) => (
        <ErrorMessage key={source.id} className="mx-5">
          {displayName(source.file)}: {t(($) => $.planning.upload.nothingFound)}
        </ErrorMessage>
      ))}

      {partial.map((source) => (
        <div key={source.id} className="pan-body mx-5 flex items-center gap-2 text-gray-600">
          <Icon icon={SpriteMap.AlertIcon} className="size-4 shrink-0 text-yellow-600" />
          {displayName(source.file)}:{' '}
          {t(($) => $.planning.upload.someUnreadable, {
            count: source.read.status === ReadStatus.Ready ? source.read.unreadable : 0
          })}
        </div>
      ))}

      {rereadFailures.map(({ source, problem }) => (
        <div key={source.id} className="pan-body mx-5 flex items-center gap-2 text-gray-600">
          <Icon icon={SpriteMap.AlertIcon} className="size-4 shrink-0 text-yellow-600" />
          {displayName(source.file)}: {t(($) => $.planning.upload.rereadFailed)}{' '}
          {t(($) => $.planning.upload.unreadable[problem])}
        </div>
      ))}

      {rows.length > 0 && (
        <>
          {props.destination === DestinationKind.Operation && (
            <>
              <hr className="mx-5 border-gray-200" />
              <h3 className="pan-title5 px-5 pt-4 uppercase text-gray-900">
                {t(($) =>
                  state.submission.status === SubmitStatus.Settled
                    ? $.planning.upload.uploadFinished
                    : $.planning.upload.pendingUploads
                )}
              </h3>
            </>
          )}

          <ImportBand {...bandProps} />

          <PlanEntryRowHeader
            destination={props.destination}
            allIncluded={included.length === rows.length}
            disabled={locked}
            onToggleAll={(excluded) =>
              dispatch({ type: PlanUploadActionType.AllExcluded, excluded })
            }
          />
          <div className="flex flex-col">
            {rows.map((row) => (
              <PlanEntryRow
                key={row.entry.id}
                row={row}
                placement={placementFor(row)}
                importMeasureSystem={importMeasureSystem}
                locked={locked}
                onRename={(name) =>
                  dispatch({ type: PlanUploadActionType.Renamed, entryId: row.entry.id, name })
                }
                onReadAs={(reading) => readAs(row.source, [{ entryIds: [row.entry.id], reading }])}
                onToggle={(excluded) =>
                  dispatch({ type: PlanUploadActionType.Excluded, entryId: row.entry.id, excluded })
                }
              />
            ))}
          </div>

          {rows.length >= ROWS_WORTH_REPEATING && <ImportBand {...bandProps} />}
        </>
      )}
    </div>
  )
}
